import {FC, useEffect, useState} from 'react';
import {Box, Drawer, Typography, useMediaQuery} from '@mui/material';
import {DOLLAR, H6, H4} from '@/utils/constants';
import {fund} from './fund.styles';
import FundItem from '@/components/Organization/Fund/FundItem';
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {toast} from 'react-toastify';
import {getErrorMessage, isValidAmount} from '@/utils/helpers';
import {getDonationListByEvent, userMakeDonation} from '@/store/user-donation/actions';
import {donationsData, totalDonationsAmount} from '@/store/user-donation/selector';
import {ReactComponent as MoneyIcon} from '@icons/sidebar/money.svg';
import {ReactComponent as SuccessIcon} from '@icons/successfully.svg';
import {ReactComponent as DangerIcon} from '@icons/danger.svg';
import Button from '@/shared/components/Button';
import {getCurrentEventSelector} from '@/store/organization-event';
import {userSidebarStyle} from '@/layouts/UserTemplate/UserSidebar/UserSidebar.styles';
import Modal from '@/shared/components/Modal';
import MakeDonationModal, {donationOptions} from '@/components/User/MakeDonationModal';
import {getUserInfo, getUserSelector, userLogin} from '@/store/user';
import NeedBeLoggedModal from '@/components/User/NeedBeLogged';
import LoginForm from '@/components/Organization/Login/LoginForm';
import {PAYMENT_METHOD, USER_TYPE} from '@/utils/enums';
import {OrganizationAttendeesService, userStorage} from '@/utils/services';
import {LoginFormValues} from '@/components/Organization/Login/LoginForm/formConfiguration';
import {userAPI} from '@/core/api';
import {usePaymentStatusPolling} from '@/utils/hooks';
import {homeStyle} from '../Home/Home.styles';
import {ReactComponent as PaymentIcon} from '@icons/payment-method.svg';
import PaymentOptions from '@/pages/User/Profile/General/PaymentOptions';
import {profileContactList} from '@/pages/User/Profile/General/ProfileGeneral.style';
import {useCardAttachment, WaitResult} from '@/utils/hooks/useCardAttachment';
import axios from 'axios';
import { useLocation } from 'react-router-dom';

const getCardErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    if ((error.response?.status ?? 0) >= 500) {
      return 'Something went wrong on our side. Please try again later.';
    }
    return getErrorMessage(error);
  }
  return error instanceof Error ? error.message : 'Something went wrong. Please try again later.';
};

const FundTheMission: FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();

  const user = useAppSelector(getUserSelector);
  const event = useAppSelector(getCurrentEventSelector);
  const donations = useAppSelector(donationsData);
  const totalAmount = useAppSelector(totalDonationsAmount);

  const {isStarting, isWaiting, waitForAttachment, startAttachment} = useCardAttachment(event?.id, location.pathname);

  const [isDonationModalOpen, setIsDonationModalOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(donationOptions[0]);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isNeedBeLoggedModalOpen, setIsNeedBeLoggedModalOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [isAttentionModalOpen, setIsAttentionModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalDescription, setModalDescription] = useState('');
  const [isShowPaymentModal, setIsShowPaymentModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState<1 | 2>(1);
  const [paymentOption, setPaymentOption] = useState(
    user?.paymentMethod ? user.paymentMethod : PAYMENT_METHOD.CREDIT_CARD
  );

  const isLaptop = useMediaQuery('(min-width:1200px)');

  const {startPollingFromUrl} = usePaymentStatusPolling({
    onCompleted: () => {
      getDonationsListData();
      setIsSuccessModalOpen(true);
    },
    apiInstance: userAPI,
  });

  const handleNextStep = () => setPaymentStep(2);
  const handleClosePaymentModal = () => {
    setIsShowPaymentModal(false);
    setPaymentStep(1);
  };

  // ---------- Payment: старе (не-карткові способи) ----------
  const addPaymentOption = async () => {
    if (!user?.bidderId || !user?.id) return;
    try {
      const res = await OrganizationAttendeesService.addAttendeePaymentStatus(user.bidderId, paymentOption);
      if (res.status === 204) {
        await dispatch(getUserInfo(user?.id)).unwrap();
        toast.success('Payment method added successfully!');
      }

      handleClosePaymentModal();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // ---------- Payment: Stripe-флоу для картки (NEW) ----------
  const syncUser = async () => {
    if (user?.id) await dispatch(getUserInfo(user.id)).unwrap();
  };

  const handleWaitResult = async (result: WaitResult) => {
    if (result === 'attached') {
      await syncUser();
      toast.success('Payment method added successfully!', {toastId: 'card-attached'});
    }
    if (result === 'timeout') {
      toast.error('Card verification is taking longer than expected. Please refresh the page in a moment.', {
        toastId: 'card-timeout',
        autoClose: 8000,
      });
    }
  };

  const addCardViaStripe = async () => {
    try {
      const result = await startAttachment();
      if (result === 'redirect') return;

      handleClosePaymentModal();

      if (result === 'attached') {
        await syncUser();
        toast.success('Payment method is already attached');
        return;
      }

      // processing
      await handleWaitResult(await waitForAttachment());
    } catch (error) {
      handleClosePaymentModal();
      toast.error(getCardErrorMessage(error), {toastId: 'card-setup-error', autoClose: 8000});
    }
  };

  const handleConfirmPayment = () =>
    paymentOption === PAYMENT_METHOD.CREDIT_CARD ? addCardViaStripe() : addPaymentOption();

  // NEW: тост обробки картки
  useEffect(() => {
    if (!isWaiting) return undefined;

    const toastId = toast.loading('We are processing your card. This may take a few seconds...');

    return () => {
      toast.dismiss(toastId);
    };
  }, [isWaiting]);

  const getDonationsListData = async () => {
    if (!event?.id) return;

    try {
      await dispatch(getDonationListByEvent({reqBody: event?.id, apiInstance: userAPI})).unwrap();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleAttentionModalChange = (title: string, description: string) => {
    if (title && description) {
      setModalTitle(title);
      setModalDescription(description);
    }
    setIsAttentionModalOpen(!isAttentionModalOpen);
  };

  const handleMakeDonationModalShow = () => {
    if (!user) {
      setIsNeedBeLoggedModalOpen(true);
    } else if (!event?.isFundTheMission) {
      handleAttentionModalChange(
        'Thank You for Wanting to Help!',
        'The organizer has paused donation for this auction.'
      );
    } else if (!user.paymentMethod) {
      setIsShowPaymentModal(true);
    } else {
      setIsDonationModalOpen(true);
      setSelectedOption(donationOptions[0]);
    }
  };

  const handleAddDonation = async () => {
    if (!user || !event) return;

    if (!isValidAmount(selectedOption)) {
      return setError('Please enter valid donation amount');
    }

    const reqBody = {
      amount: Number(selectedOption),
      eventId: event.id,
    };

    try {
      await dispatch(userMakeDonation({reqBody, apiInstance: userAPI})).unwrap();
      await dispatch(getDonationListByEvent({reqBody: event?.id, apiInstance: userAPI})).unwrap();

      setIsSuccessModalOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsDonationModalOpen(false);
      setError('');
    }
  };

  const handleSubmitClickHandler = async (values: LoginFormValues) => {
    try {
      await dispatch(userLogin(values)).unwrap();
      userStorage.set('userType', USER_TYPE.USER);
      setIsLoginModalOpen(false);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    getDonationsListData();
  }, [event?.id]);

  useEffect(() => {
    startPollingFromUrl();
  }, []);

  return (
    <Box sx={fund.container}>
      <Box sx={fund.totalWrap}>
        <Typography variant={H6} sx={fund.totalLabel}>
          Total
        </Typography>
        <Typography variant={H4} sx={fund.totalValue}>
          {DOLLAR}
          {totalAmount || 0}
        </Typography>
      </Box>
      <Box sx={fund.fundList}>{donations && donations.map((el) => <FundItem key={el.donationId} item={el} />)}</Box>

      {isLaptop ? (
        <Box sx={fund.popup}>
          <Button
            title="Make a Donation"
            onClick={handleMakeDonationModalShow}
            startIcon={
              <Box sx={fund.moneyIconWrapper}>
                <MoneyIcon />
              </Box>
            }
          />
        </Box>
      ) : (
        <Drawer variant="permanent" anchor="bottom" sx={fund.drawer}>
          <Box sx={fund.popup}>
            <Button
              title="Make a Donation"
              onClick={handleMakeDonationModalShow}
              startIcon={
                <Box sx={fund.moneyIconWrapper}>
                  <MoneyIcon />
                </Box>
              }
            />
          </Box>
        </Drawer>
      )}
      <Modal
        isShowSaveButton
        mainButtonTitle={`Donate $${selectedOption}`}
        isShowCloseButton
        closeButtonTitle="Close"
        onClose={() => {
          setIsDonationModalOpen(false);
          setError('');
        }}
        open={isDonationModalOpen}
        sx={userSidebarStyle.modalBid}
        onSave={handleAddDonation}
      >
        <MakeDonationModal selectedOption={selectedOption} setSelectedOption={setSelectedOption} error={error} />
      </Modal>
      <Modal
        open={isSuccessModalOpen}
        onClose={() => {
          setIsSuccessModalOpen(false);
          setSelectedOption(donationOptions[0]);
        }}
        title="Thank You for Your Donation"
        description={`Your donation of $${selectedOption} successfully processed.`}
        sx={userSidebarStyle.modalLogin}
        closeButtonTitle="Done"
        mainIcon={<SuccessIcon />}
      />
      <Modal
        onClose={() => setIsNeedBeLoggedModalOpen(false)}
        open={isNeedBeLoggedModalOpen}
        sx={userSidebarStyle.modalLogin}
        mainIcon={<DangerIcon />}
      >
        <NeedBeLoggedModal handleClose={() => setIsNeedBeLoggedModalOpen(false)} />
      </Modal>
      <Modal onClose={() => setIsLoginModalOpen(false)} open={isLoginModalOpen} sx={userSidebarStyle.modal}>
        <LoginForm handleSubmitClickHandler={handleSubmitClickHandler} userType={USER_TYPE.USER} />
      </Modal>
      <Modal
        open={isAttentionModalOpen}
        onClose={() => handleAttentionModalChange('', '')}
        title={modalTitle}
        description={modalDescription}
        sx={homeStyle.modalLogin}
      />
      {paymentStep === 1 ? (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Add Payment Method"
          description="Adding a payment method allows you to participate in the auction and make donations."
          isShowCloseButton
          closeButtonTitle="Later"
          mainButtonTitle="Add Payment Method"
          isShowSaveButton
          onSave={handleNextStep}
          mainIcon={<PaymentIcon />}
          sx={profileContactList.modal}
        />
      ) : (
        <Modal
          open={isShowPaymentModal}
          onClose={handleClosePaymentModal}
          title="Choose Payment Option"
          isShowCloseButton
          closeButtonTitle="Cancel"
          mainButtonTitle="Confirm"
          isShowSaveButton
          isSaveButtonDisable={isStarting}
          onSave={handleConfirmPayment}
          sx={profileContactList.modal}
        >
          <PaymentOptions value={paymentOption} onChange={setPaymentOption} />
        </Modal>
      )}
    </Box>
  );
};

export default FundTheMission;
